// KBDescriptionPopup.tsx (or paste into same file above return)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChangeEventArgs, HtmlEditor, Inject, RichTextEditorComponent, Toolbar } from '@syncfusion/ej2-react-richtexteditor';
import { PositionDataModel } from '@syncfusion/ej2-react-popups';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import FacilityActions from '../actions/FacilityActions';
import ViewerActions from '@facility/actions/ViewerActions';
import UrlUtilities from '@shared/common/UrlUtilities';
import ComponentHelpers from '@facility/helpers/ComponentHelpers';
import ViewerStore from '@facility/stores/ViewerStore';
import FacilityStore from '@facility/stores/FacilityStore';

interface KBDescriptionPopupProps {
    kb?: IKnowledgeBook | null;
    kv?: IKnowledgeViewDetail | null
    dialogPosition: PositionDataModel;
    onClose: () => void;         // invoked when user clicks X
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

const KBDescriptionPopup: React.FC<KBDescriptionPopupProps> = ({ kb, kv, dialogPosition, onClose }) => {
    const mountedRef = useRef(false);
    const rteRef = useRef(null);
    const [isKBPopupPinned, setIsKBPopupPinned] = useState<boolean>(
        KnowledgeBookStore.getKBPopupPinnedStatus()
    );
    const [favoriteKnowledgeBookIds, setFavoriteKnowledgeBookIds] = useState<string[]>(
        KnowledgeBookStore.getFavoriteKnowledgeBookIds()
    );
    const [kbViewingMode, setKbViewingMode] = useState<KnowledgeBookViewingMode>(KnowledgeBookStore.getKnowledgeBookViewingMode());
    const [knowledgeBookViews, setKnowledgeBookViews] = useState<IKnowledgeView[]>(
        KnowledgeBookStore.getAllKnowledgeView()
    );
    const [facilityAssetSetsBarOpen, setFacilityAssetSetsBarOpen] = useState(
        FacilityStore.getFacilityAssetSetsBarOpen()
    );
    const canModifyKnowledgeView = ComponentHelpers.hasModifyKnowledgeBookPermission(kb);
    const [expandedKvDescription, setExpandedKvDescription] = useState(true);
    const [expanded, setExpanded] = useState(false);

    const getDescriptionToggleTitle = (isExpanded: boolean) => {
        const translationKey = isExpanded
            ? "facility:knowledgeBook.hideKnowledgeBookDescription"
            : "facility:knowledgeBook.showKnowledgeBookDescription";
        return InovxLoader.t(translationKey);
    }

    // Keep expanded true while mouse over; collapse when leaves
    const handleEnter = () => setExpanded(true);
    const handleLeave = () => setExpanded(false);

    const onChange = useCallback(() => {
        if (!mountedRef.current) return;
        setKbViewingMode(KnowledgeBookStore.getKnowledgeBookViewingMode())
        setIsKBPopupPinned(KnowledgeBookStore.getKBPopupPinnedStatus());
        setKnowledgeBookViews(KnowledgeBookStore.getAllKnowledgeView());
        setFavoriteKnowledgeBookIds(KnowledgeBookStore.getFavoriteKnowledgeBookIds());
        setFacilityAssetSetsBarOpen(FacilityStore.getFacilityAssetSetsBarOpen());
    }, []);
    useEffect(() => {
        mountedRef.current = true;

        KnowledgeBookStore.addChangeListener(onChange);
        FacilityStore.addChangeListener(onChange);

        return () => {
            mountedRef.current = false;
            KnowledgeBookStore.removeChangeListener(onChange);
            FacilityStore.removeChangeListener(onChange);
        };
    }, [onChange]);

    if (!kb) return null;

    useEffect(() => {
        KnowledgeBookActions.getFavoriteKnowledgeBookIds();
    }, [kb.ID]);

    const pinUnpinKBPopup = () => {
        if (!isKBPopupPinned) FacilityActions.closeKBbar();
        KnowledgeBookActions.pinUnpinKBPopup(!isKBPopupPinned)
    }

    const toggleFavoriteKnowledgeBook = async () => {
        await KnowledgeBookActions.toggleFavoriteKnowledgeBook(kb);
    };

    const isFavorite = favoriteKnowledgeBookIds.indexOf(kb.ID) >= 0;

    const currentKVIndex = React.useMemo(() => {
        if (!kv) return -1;
        return knowledgeBookViews.findIndex(
            (item) => item.ID === kv.ID
        );
    }, [knowledgeBookViews, kv]);

    const hasUnsavedData = (): boolean => {
        return ViewerActions.checkForKnowledgeViewChanges();
    }

    const hasPrevious = currentKVIndex > 0;
    const hasNext = currentKVIndex !== -1 && currentKVIndex < knowledgeBookViews.length - 1;

    const openPreviousView = async () => {
        if (!hasPrevious) return;
        if(hasUnsavedData()) return;
        const prevKV = knowledgeBookViews[currentKVIndex - 1];
        await FacilityActions.openKnowledgeViewInViewer(prevKV);
    };

    const openView = async () => {
        const currentKV = knowledgeBookViews[0];
        await FacilityActions.openKnowledgeViewInViewer(currentKV);
    };

    const openNextView = async () => {
        if (!hasNext) return;
        if(hasUnsavedData()) return;
        const nextKV = knowledgeBookViews[currentKVIndex + 1];
        await FacilityActions.openKnowledgeViewInViewer(nextKV);
    };

    const openKnowledgeViewForm = React.useCallback(() => {
        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (selectedKnowledgeView) {
            KnowledgeBookActions.beginEditKnowledgeView(selectedKnowledgeView);
        }
    }, []);

    const KnowledgeViewDescription = (description: string) => {
        if (!description) return;
        return (<div className={`kv-description ${expanded || isKBPopupPinned ? 'kb-expanded' : ''}`}>
            <RichTextEditorComponent
                ref={rteRef}
                readonly={true}
                value={description}
                cssClass="kb-rte-transparent editor-min-height">
                <Inject services={[HtmlEditor]} />
            </RichTextEditorComponent>
        </div>)
    }
    return (
        <div
            className="kb-desc-popup-wrapper"
            style={{ left: dialogPosition?.X ?? 67, top: dialogPosition?.Y ?? 62 }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            aria-live="polite"
        >
            <div className={`hover-box ${expanded || isKBPopupPinned ? 'kb-expanded' : ''}`}>
                <div className="kb-desc-header">
                    <div className="kb-desc-title" title={kb.Name}>{kb.Name}</div>
                    <div className="kb-action">
                        <button
                            className={`kb-btn kb-btn-favorite${isFavorite ? ' active' : ''}`}
                            title={isFavorite ? InovxLoader.t("facility:knowledgeBook.removeFavorite") : InovxLoader.t("facility:knowledgeBook.addFavorite")}
                            onClick={() => toggleFavoriteKnowledgeBook()}>
                            <span className={`fa ${isFavorite ? 'fa-star' : 'fa-star-o'}`} aria-hidden="true" />
                        </button>
                        <button className={`kb-btn`} title={isKBPopupPinned ? InovxLoader.t("facility:knowledgeBook.unpinKbPopup") : InovxLoader.t("facility:knowledgeBook.pinKbPopup")} onClick={() => pinUnpinKBPopup()} >
                            <span className={`kb-action-icon ${isKBPopupPinned ? 'btn-unpin' : 'btn-pin'}`}></span>
                        </button>
                        <button className="kb-desc-close" title={InovxLoader.t("facility:knowledgeBook.closeKbDescription")} onClick={onClose} />
                    </div>
                </div>
                <div className={`${(kb.Description || kbViewingMode === 'manageKV') ? "box-description" : ""}`}>
                    {kb.Description && <div className="kb-description">
                        {kb.Description}
                    </div>}
                    {kbViewingMode === 'manageKV' && <>{(kv) ?
                        <div className="d-flex j-csb a-ic mt-5px">
                            <div className="kv-details-row">
                                <div className="kv-title" title={kv.Name}>{kv.Name}</div>
                                {canModifyKnowledgeView && <button
                                    className="kb-btn kv-edit-button"
                                    title={InovxLoader.t('facility:action.editView')}
                                    onClick={() => openKnowledgeViewForm()}>
                                        <img src={`${UrlUtilities.getBaseApplicationUrl()}images/KB/Pen.png`} alt={InovxLoader.t('facility:action.editView')} />
                                    {/* <i className="fa fa-pencil" aria-hidden="true"></i> */}
                                </button>}
                            </div>
                            <div className="kv-navigation">
                                <button
                                    className={`kv-btn btn-prev${!hasPrevious ? '-disabled' : ''}`}
                                    title={InovxLoader.t("facility:knowledgeBook.openPreviousView")}
                                    onClick={openPreviousView}
                                />
                                <button
                                    className={`kv-btn ml-10 btn-next${!hasNext ? '-disabled' : ''}`}
                                    title={InovxLoader.t("facility:knowledgeBook.openNextView")}
                                    onClick={openNextView}
                                />
                            </div>
                        </div> : knowledgeBookViews.length > 0 && !facilityAssetSetsBarOpen && <div className="d-flex j-csb a-ic mt-5px">
                            <div className="kv-title muted" title={knowledgeBookViews[0].Name}>{knowledgeBookViews[0].Name}</div>
                            <div className="d-flex j-csb">
                                <button
                                    className="kv-btn btn-open"
                                    title={InovxLoader.t("facility:knowledgeBook.openView")}
                                    onClick={openView}
                                />
                            </div>
                        </div>}
                    </>}
                    
                </div>
                {(kbViewingMode === 'manageKV' && kv && kv.Description && (expanded || isKBPopupPinned)) && <button
                    className={`no-btn btn-bottom`}
                    title={getDescriptionToggleTitle(expandedKvDescription)}
                    onClick={() => setExpandedKvDescription(!expandedKvDescription)}
                >
                    <i className={`btn-icon ${expandedKvDescription ? 'up-arrow' : 'down-arrow'}`} />
                </button>}
            </div>
            {(kbViewingMode === 'manageKV' && kv?.Description && expandedKvDescription) && KnowledgeViewDescription(kv.Description)}
        </div>
    );
};

export default KBDescriptionPopup;
